import { useEffect, useMemo, useState, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Badge, Table, Tooltip, Typography } from 'antd'
import type { ColumnsType, TableProps } from 'antd/es/table'
import { debounce } from 'lodash'
import { DateTime } from 'luxon'
import styled from 'styled-components'

import { colours, sizes } from '@qflow/theme'

import type { InfractionAlertType } from 'portal/components/RecordsModules/common/alerts'
import { getRecordLinkUri } from 'portal/components/RecordsTable/RecordsColumn/RecordLink'

import { DateFormatFull } from 'portal/lib/functions'
import { icons, Spacer, UserAvatar } from 'portal/lib/primitives'

import { ActionStrip } from './ActionStrip'
import { SubmissionErrorsDescriptionFilters } from './InfractionTableHelpers'
import {
  BTTButton,
  ButtonContainer,
  ShowingAlertsText,
  StyledClickableLink,
  TableWrapper,
  TableFooter,
  ZeroRecordsFooter
} from './StyledElements'

export const InfractionsTable = ({
  allowSelectionAndAction,
  reload,
  moduleName,
  handlePageChange,
  hasNextPage,
  totalAlertsCount,
  filteredInfo,
  setFilteredInfo
}) => {
  const [filteredDataCount, setFilteredDataCount] = useState(0)

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [eventRecordMapping, setEventRecordMapping] = useState({})
  const [assignableUsers, setAssignableUsers] = useState<any[]>([])

  const { accountId, projectId } = useParams<{
    accountId: string
    projectId: string
  }>()

  const CONSTANTS = {
    CLOSED: 'Closed'
  }

  const projectTimeZone = useSelector(
    (state: any) => state.projectDetails.modules?.timeZone
  )

  const projectUsers = useSelector((state: any) => state.projectUsers)

  type User = {
    userId: string
    displayName: string
  }

  const users: User[] = Object.values(projectUsers.users)

  const { items } = useSelector(
    (state: any) => state.events[moduleName ? moduleName : 'submission']
  )

  const {
    description,
    tradeContractor,
    wasteCarrier,
    subcontractor,
    supplier
  } = useSelector(
    (state: any) =>
      state.events[`${moduleName ? moduleName : 'submission'}Filter`]
  )

  const { Text } = Typography

  // Create an object whose keys is the eventKey
  // and value is an object containing the recordId, and
  // an array of who is assigned to that record
  useEffect(() => {
    const tempMapping = {}

    items.forEach(item => {
      tempMapping[item.eventKey] = {
        recordId: item.recordId,
        assignedTo: item.assignedTo,
        description: item.description,
        subcontractor: item.subcontractor,
        wasteCarrier: item.wasteCarrier,
        isRecordLevelAlert: item.isRecordLevelAlert
      }
    })

    setEventRecordMapping(tempMapping)
  }, [items])

  // Reformat ProjectUsers for use in AssigneeIds filter
  const UNASSIGNED = 'unassigned'
  useEffect(() => {
    const otherAssignedToFilters = [
      { value: UNASSIGNED, text: <i>Unassigned</i> }
    ]

    const tempArray = Object.values(projectUsers.users)
      .map((user: any) => ({
        value: user.userId as string,
        text: user.displayName as string
      }))
      .sort((a, b) => {
        if (a.text === null) {
          return 1
        }
        if (b.text === null) {
          return -1
        }
        const fa = a.text.toLowerCase()
        const fb = b.text.toLowerCase()

        if (fa < fb) {
          return -1
        } else if (fa > fb) {
          return 1
        }
        return 0
      })

    setAssignableUsers([...otherAssignedToFilters, ...tempArray])
  }, [projectUsers.users])

  const tableId = 'AlertsTable'

  // Infinite Scroll
  useEffect(() => {
    const handleTableScroll = event => {
      const maxScroll = event.target.scrollHeight - event.target.clientHeight
      const currentScroll = event.target.scrollTop
      if (currentScroll >= maxScroll - 65) {
        // load more data
        handlePageChange()
      }
    }

    const debouncedHandleTableScroll = debounce(
      event => handleTableScroll(event),
      100,
      {
        leading: true
      }
    )

    const tableContent = document.querySelector('.ant-table-body')
    if (tableContent) {
      // Only apply the Event Listener if there is more data to be loaded
      if (hasNextPage) {
        tableContent.addEventListener('scroll', debouncedHandleTableScroll)
      }
    }

    // Using useEffect return function to remove the Event Listener on unload
    return () => {
      if (tableContent) {
        tableContent.removeEventListener('scroll', debouncedHandleTableScroll)
      }
    }
  }, [handlePageChange, hasNextPage])

  const scrollToTop = () => {
    const tableContent = document.querySelector('.ant-table-body')
    // Check if the tableContent is null
    if (!tableContent) return

    // Scroll to the top of the table
    tableContent.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    })
  }

  let usedHeight = 370
  const beamerAnnouncementBar = document.getElementById('beamerAnnouncementBar')
  if (beamerAnnouncementBar) {
    usedHeight += beamerAnnouncementBar.getBoundingClientRect().height
  }

  const renderDescription = (text, item, index) => {
    if (index === 0 || !item.subDescription) {
      return text
    }
    return (
      <div>
        <div>{text}</div>
        <div className="subText">{item.subDescription}</div>
      </div>
    )
  }

  const handleResetFilters = useCallback(() => {
    setFilteredInfo({})
  }, [setFilteredInfo])

  // If all filters are null, or empty array, disable the reset button
  const allFiltersEmpty = Object.entries(filteredInfo).every(
    ([key, filter]) => {
      return filter === null || (Array.isArray(filter) && filter.length === 0)
    }
  )

  // Count the number of active filters to display in the badge
  const activeFilterCount = Object.entries(filteredInfo).reduce(
    (count, [key, filter]) => {
      if (
        (typeof filter === 'string' && filter !== '') ||
        (Array.isArray(filter) && filter.length > 0)
      ) {
        return count + 1
      }
      return count
    },
    0
  )

  const columns: ColumnsType<InfractionAlertType> = useMemo(() => {
    function setCellId(alert, rowIndex) {
      if (alert.status === CONSTANTS.CLOSED) {
        return {
          style: {
            textDecoration: 'line-through',
            opacity: '0.66'
          }
        }
      }
      return {}
    }

    if (moduleName === 'delivery') {
      return [
        {
          title: 'Date Created',
          dataIndex: 'createdAt',
          className: 'createdAt',
          key: 'createdAt',
          sorter: (a, b) => {
            const aValue = a.createdAt || ''
            const bValue = b.createdAt || ''
            return aValue.localeCompare(bValue)
          },
          defaultSortOrder: 'descend',
          onCell: setCellId,
          width: '8.5rem',
          render: (text, record) => {
            return DateTime.fromISO(text, { zone: 'local' }).toLocaleString(
              DateFormatFull(projectTimeZone) as Intl.DateTimeFormatOptions,
              { locale: Intl.DateTimeFormat().resolvedOptions().locale }
            )
          }
        },
        {
          title: 'Description',
          dataIndex: 'description',
          className: 'description',
          key: 'description',
          onCell: setCellId,
          filters: description
            ? description.map(d => ({
                text: d,
                value: d
              }))
            : [],
          filteredValue: filteredInfo.description || null,
          filterSearch: true,
          onFilter: (value, record) => {
            if (record.description) {
              return record.description.includes(value as string)
            }
            return false
          },
          render: (text, item, index) => renderDescription(text, item, index)
        },
        {
          title: 'Delivery ID',
          dataIndex: 'destinationDisplayName',
          key: 'destinationDisplayName',
          sorter: (a, b) => {
            const aValue = a.destinationDisplayName || ''
            const bValue = b.destinationDisplayName || ''
            return aValue.localeCompare(bValue)
          },
          onCell: setCellId,
          render: text => {
            return <Text>{text ?? '-'}</Text>
          }
        },
        {
          title: 'Trade Contractor',
          dataIndex: 'tradeContractor',
          className: 'tradeContractor',
          key: 'tradeContractor',
          sorter: (a, b) => {
            const aValue = a.tradeContractor || ''
            const bValue = b.tradeContractor || ''
            return aValue.localeCompare(bValue)
          },
          onCell: setCellId,
          filters: tradeContractor
            ? tradeContractor.map(tc => ({
                text: tc,
                value: tc
              }))
            : [],
          filteredValue: filteredInfo.tradeContractor || null,
          filterSearch: true,
          onFilter: (value, record) => {
            if (record.tradeContractor) {
              return record.tradeContractor.includes(value as string)
            }
            return false
          },
          render: text => {
            return <Text>{text ?? '-'}</Text>
          }
        },
        {
          title: 'Supplier Company',
          dataIndex: 'supplier',
          className: 'supplier',
          key: 'supplier',
          sorter: (a, b) => {
            const aValue = a.supplier || ''
            const bValue = b.supplier || ''
            return aValue.localeCompare(bValue)
          },
          onCell: setCellId,
          filters: supplier
            ? supplier.map(s => ({
                text: s,
                value: s
              }))
            : [],
          filteredValue: filteredInfo.supplier || null,
          filterSearch: true,
          onFilter: (value, record) => {
            if (record.supplier) {
              return record.supplier.includes(value as string)
            }
            return false
          },
          render: text => {
            return <Text>{text ?? '-'}</Text>
          }
        },
        {
          title: 'Assigned To',
          dataIndex: 'assignedTo',
          className: 'assignedTo',
          filters: assignableUsers,
          filteredValue: filteredInfo.assignedTo || null,
          filterSearch: true,
          onFilter: (value, record) => {
            if (record.assignedTo && record.assignedTo.length > 0) {
              return record.assignedTo.some(user => user.userId === value)
            }
            return value === UNASSIGNED
          },
          width: '8rem',
          align: 'center',
          render: (assignedToArray, record) => {
            const userAvatars = assignedToArray?.map((user: any) => {
              const matchedUser: any = users
                ? users.find((u: any) => u.userId === user.userId)
                : null

              return matchedUser ? (
                <UserAvatar displayName={matchedUser.displayName} />
              ) : (
                <UserAvatar displayName={'Qflow System'} />
              )
            })
            return userAvatars
          }
        },
        {
          title: (
            <Tooltip title="Reset filters">
              <ResetFilters
                aria-label="Reset filters"
                disabled={allFiltersEmpty}
                onClick={handleResetFilters}
              >
                <Badge
                  count={allFiltersEmpty ? 0 : activeFilterCount}
                  offset={[-1, 1]}
                  color={colours.CONSTRUCTION_ORANGE}
                  styles={{
                    root: {
                      color: 'inherit'
                    }
                  }}
                >
                  <icons.ResetFilters size="22px" />
                </Badge>
              </ResetFilters>
            </Tooltip>
          ),
          width: '4rem',
          dataIndex: 'recordId',
          className: 'links',
          key: 'recordId',
          render: (text, record) => {
            if (!accountId || !projectId || !text) {
              return null
            }
            return (
              <StyledClickableLink
                id="navigateToDetailsBtn"
                to={getRecordLinkUri(
                  {
                    accountId,
                    projectId,
                    moduleName,
                    module: moduleName
                  },
                  moduleName,
                  record.recordId
                )}
              >
                <Tooltip title="View record">
                  <icons.ViewRecord />
                </Tooltip>
              </StyledClickableLink>
            )
          }
        }
      ]
    } else if (moduleName === 'waste') {
      return [
        {
          title: 'Date Created',
          dataIndex: 'createdAt',
          className: 'createdAt',
          key: 'createdAt',
          sorter: (a, b) => {
            const aValue = a.createdAt || ''
            const bValue = b.createdAt || ''
            return aValue.localeCompare(bValue)
          },
          defaultSortOrder: 'descend',
          onCell: setCellId,
          width: '8.5rem',
          render: (text, record) => {
            return DateTime.fromISO(text, { zone: 'local' }).toLocaleString(
              DateFormatFull(projectTimeZone) as Intl.DateTimeFormatOptions,
              { locale: Intl.DateTimeFormat().resolvedOptions().locale }
            )
          }
        },
        {
          title: 'Description',
          dataIndex: 'description',
          className: 'description',
          key: 'description',
          onCell: setCellId,
          filters: description
            ? description.map(d => ({
                text: d,
                value: d
              }))
            : [],
          filteredValue: filteredInfo.description || null,
          filterSearch: true,
          onFilter: (value, record) => {
            if (record.description) {
              return record.description.includes(value as string)
            }
            return false
          },
          render: (text, item, index) => renderDescription(text, item, index)
        },
        {
          title: (
            <span>
              Consignment N<sup>o</sup>
            </span>
          ),
          dataIndex: 'destinationDisplayName',
          className: 'destinationDisplayName',
          key: 'destinationDisplayName',
          sorter: (a, b) => {
            const aValue = a.destinationDisplayName || ''
            const bValue = b.destinationDisplayName || ''
            return aValue.localeCompare(bValue)
          },
          onCell: setCellId,
          render: text => {
            return <Text>{text ?? '-'}</Text>
          }
        },
        {
          title: 'Subcontractor',
          dataIndex: 'subcontractor',
          className: 'subcontractor',
          key: 'subcontractor',
          sorter: (a, b) => {
            const aValue = a.subcontractor || ''
            const bValue = b.subcontractor || ''
            return aValue.localeCompare(bValue)
          },
          onCell: setCellId,
          filters: subcontractor
            ? subcontractor.map(sc => ({
                text: sc,
                value: sc
              }))
            : [],
          filteredValue: filteredInfo.subcontractor || null,
          filterSearch: true,
          onFilter: (value, record) => {
            if (record.subcontractor) {
              return record.subcontractor.includes(value as string)
            }
            return false
          },
          render: text => {
            return <Text>{text ?? '-'}</Text>
          }
        },
        {
          title: 'Carrier',
          dataIndex: 'wasteCarrier',
          className: 'wasteCarrier',
          key: 'wasteCarrier',
          sorter: (a, b) => {
            const aValue = a.wasteCarrier || ''
            const bValue = b.wasteCarrier || ''
            return aValue.localeCompare(bValue)
          },
          onCell: setCellId,
          filters: wasteCarrier
            ? wasteCarrier.map(wc => ({
                text: wc,
                value: wc
              }))
            : [],
          filteredValue: filteredInfo.wasteCarrier || null,
          filterSearch: true,
          onFilter: (value, record) => {
            if (record.wasteCarrier) {
              return record.wasteCarrier.includes(value as string)
            }
            return false
          },
          render: text => {
            return <Text>{text ?? '-'}</Text>
          }
        },
        {
          title: 'Assigned To',
          dataIndex: 'assignedTo',
          className: 'assignedTo',
          filters: assignableUsers,
          filteredValue: filteredInfo.assignedTo || null,
          filterSearch: true,
          onFilter: (value, record) => {
            if (record.assignedTo && record.assignedTo.length > 0) {
              return record.assignedTo.some(user => user.userId === value)
            }
            return value === UNASSIGNED
          },
          width: '8rem',
          align: 'center',
          render: (assignedToArray, record) => {
            const userAvatars = assignedToArray?.map((user: any) => {
              const matchedUser: any = users
                ? users.find((u: any) => u.userId === user.userId)
                : null

              return matchedUser ? (
                <UserAvatar displayName={matchedUser.displayName} />
              ) : (
                <UserAvatar displayName={'Qflow System'} />
              )
            })
            return userAvatars
          }
        },
        {
          title: (
            <Tooltip title="Reset filters">
              <ResetFilters
                aria-label="Reset filters"
                disabled={allFiltersEmpty}
                onClick={handleResetFilters}
              >
                <Badge
                  count={allFiltersEmpty ? 0 : activeFilterCount}
                  offset={[-1, 1]}
                  color={colours.CONSTRUCTION_ORANGE}
                  styles={{
                    root: {
                      color: 'inherit'
                    }
                  }}
                >
                  <icons.ResetFilters size="22px" />
                </Badge>
              </ResetFilters>
            </Tooltip>
          ),
          width: '4rem',
          dataIndex: 'recordId',
          className: 'links',
          key: 'recordId',
          render: (text, record) => {
            if (!accountId || !projectId || !text) {
              return null
            }
            return (
              <StyledClickableLink
                id="navigateToDetailsBtn"
                to={getRecordLinkUri(
                  {
                    accountId,
                    projectId,
                    moduleName,
                    module: moduleName
                  },
                  moduleName,
                  record.recordId
                )}
              >
                <Tooltip title="View record">
                  <icons.ViewRecord />
                </Tooltip>
              </StyledClickableLink>
            )
          }
        }
      ]
    } else {
      return [
        {
          title: 'Date Created',
          dataIndex: 'createdAt',
          className: 'createdAt',
          key: 'createdAt',
          sorter: (a, b) => {
            const aValue = a.createdAt || ''
            const bValue = b.createdAt || ''
            return aValue.localeCompare(bValue)
          },
          defaultSortOrder: 'descend',
          onCell: setCellId,
          width: '8.5rem',
          render: (text, record) => {
            return DateTime.fromISO(text, { zone: 'local' }).toLocaleString(
              DateFormatFull(projectTimeZone) as Intl.DateTimeFormatOptions,
              { locale: Intl.DateTimeFormat().resolvedOptions().locale }
            )
          }
        },
        {
          title: 'Module',
          dataIndex: 'moduleName',
          key: 'moduleName',
          onCell: setCellId,
          width: '6.25rem',
          render: text => {
            return <CapitalisedText>{text}</CapitalisedText>
          },
          filters: [
            { text: 'Delivery', value: 'Delivery' },
            { text: 'Waste', value: 'Waste' }
          ],
          filteredValue: filteredInfo.moduleName || null,
          onFilter: (value, record) => record.moduleName === value
        },
        {
          title: 'Description',
          dataIndex: 'description',
          className: 'description',
          key: 'description',
          onCell: setCellId,
          filters: SubmissionErrorsDescriptionFilters,
          filteredValue: filteredInfo.description || null,
          filterSearch: true,
          onFilter: (value, record) => {
            if (record.description) {
              return record.description.startsWith(value as string)
            }
            return false
          },
          render: (text, item, index) => renderDescription(text, item, index)
        },
        {
          title: 'Assigned To',
          dataIndex: 'assignedTo',
          className: 'assignedTo',
          filters: assignableUsers,
          filteredValue: filteredInfo.assignedTo || null,
          filterSearch: true,
          onFilter: (value, record) => {
            if (record.assignedTo && record.assignedTo.length > 0) {
              return record.assignedTo.some(user => user.userId === value)
            }
            return value === UNASSIGNED
          },
          width: '8rem',
          align: 'center',
          render: (assignedToArray, record) => {
            const userAvatars = assignedToArray?.map((user: any) => {
              const matchedUser: any = users
                ? users.find((u: any) => u.userId === user.userId)
                : null

              return matchedUser ? (
                <UserAvatar displayName={matchedUser.displayName} />
              ) : (
                <UserAvatar displayName={'Qflow System'} />
              )
            })
            return userAvatars
          }
        },
        {
          title: (
            <Tooltip title="Reset filters">
              <ResetFilters
                aria-label="Reset filters"
                disabled={allFiltersEmpty}
                onClick={handleResetFilters}
              >
                <Badge
                  count={allFiltersEmpty ? 0 : activeFilterCount}
                  offset={[-1, 1]}
                  color={colours.CONSTRUCTION_ORANGE}
                  styles={{
                    root: {
                      color: 'inherit'
                    }
                  }}
                >
                  <icons.ResetFilters size="22px" />
                </Badge>
              </ResetFilters>
            </Tooltip>
          ),
          width: '4rem',
          dataIndex: 'recordId',
          className: 'links',
          key: 'recordId',
          render: (text, record) => {
            if (!accountId || !projectId || !text) {
              return null
            }
            return (
              <StyledClickableLink
                id="navigateToDetailsBtn"
                to={getRecordLinkUri(
                  {
                    accountId,
                    projectId,
                    moduleName: record.moduleName,
                    module: record.moduleName
                  },
                  record.moduleName,
                  record.recordId
                )}
              >
                <Tooltip title="View record">
                  <icons.ViewRecord />
                </Tooltip>
              </StyledClickableLink>
            )
          }
        }
      ]
    }
  }, [
    moduleName,
    CONSTANTS.CLOSED,
    description,
    filteredInfo.description,
    filteredInfo.tradeContractor,
    filteredInfo.supplier,
    filteredInfo.assignedTo,
    filteredInfo.subcontractor,
    filteredInfo.wasteCarrier,
    filteredInfo.moduleName,
    tradeContractor,
    supplier,
    assignableUsers,
    allFiltersEmpty,
    handleResetFilters,
    activeFilterCount,
    projectTimeZone,
    Text,
    users,
    accountId,
    projectId,
    subcontractor,
    wasteCarrier
  ])

  const handleTableChange = (pagination, filters, sorter, extra) => {
    // `extra.currentDataSource` contains the filtered data
    setFilteredInfo(filters)
    setFilteredDataCount(extra.currentDataSource.length)
  }

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys)
  }

  type TableRowSelection<T extends object = object> =
    TableProps<T>['rowSelection']

  const rowSelection: TableRowSelection<any> = {
    selectedRowKeys,
    onChange: onSelectChange,
    selections: [
      {
        key: 'selectAll',
        text: 'Select all',
        onSelect: changeableRowKeys => {
          setSelectedRowKeys(changeableRowKeys)
        }
      },
      {
        key: 'deslectAll',
        text: 'Deselect all',
        onSelect: () => {
          setSelectedRowKeys([])
        }
      }
    ],
    columnWidth: '3.75rem',
    getCheckboxProps: record => ({
      disabled: record.status === 'Closed' || record.isProjectLevelAlert
    })
  }

  const clearSelection = () => {
    setSelectedRowKeys([])
  }

  return (
    <>
      <TableWrapper>
        <Table
          id={tableId}
          bordered={false}
          dataSource={items}
          rowKey={row => row.eventKey || ''}
          columns={columns}
          onChange={handleTableChange}
          rowSelection={allowSelectionAndAction ? rowSelection : undefined}
          scroll={{ y: `calc(100vh - ${usedHeight}px)` }}
          size="middle"
          sticky
          pagination={false}
          locale={{
            emptyText: (
              <ZeroRecordsFooter>
                <icons.ManageSearch />
                <div>
                  No alerts matched your search. Try increasing the date range.
                </div>
              </ZeroRecordsFooter>
            )
          }}
          footer={currentPageData => {
            setFilteredDataCount(currentPageData.length)
            return null
          }}
        />
      </TableWrapper>

      <TableFooter>
        <ShowingAlertsText data-testid="totalInPage">
          Showing {filteredDataCount} out of {totalAlertsCount()} total alert
          {items.length === 1 ? '' : 's'}
        </ShowingAlertsText>
        <ButtonContainer>
          <BTTButton id="BTTButton" onClick={scrollToTop}>
            Back to top
            <Spacer.Fine />
            <icons.ChevronRight size={sizes.SMALL} rotate={-90} />
          </BTTButton>
        </ButtonContainer>
      </TableFooter>

      <ActionStrip
        eventKeys={selectedRowKeys.map(key => String(key))}
        eventRecordMapping={eventRecordMapping}
        selectedEventsContainUnloaded={false}
        onClose={reloadNeeded => {
          clearSelection()
          if (reloadNeeded) {
            reload()
          }
        }}
      />
    </>
  )
}

const ResetFilters = styled.button`
  height: 32px;
  width: 32px;
  padding: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  border: none;
  background-color: transparent;

  &:disabled {
    cursor: not-allowed;
    color: ${colours.BATTLESHIP_GREY};
  }
`
const CapitalisedText = styled(Typography.Text)`
  text-transform: capitalize;
`
